import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Wallet, Users, Clock, CheckCircle2, TrendingUp, ArrowRight, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (n) => `${Number(n||0).toLocaleString("fr-FR")} GNF`;

export default function PaieDashboard() {
  const [dash, setDash] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = async () => {
    try {
      const [{ data: d }, { data: rs }] = await Promise.all([
        api.get("/payroll/dashboard"),
        api.get("/payroll/runs"),
      ]);
      setDash(d); setRuns(rs);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="paie-dashboard">
      <div>
        <div className="chip mb-2"><Wallet className="h-3 w-3" /><span>PAIE — TABLEAU DE BORD</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Paie du personnel</h1>
        <p className="text-slate-500 mt-1">Enseignants (à l&apos;heure) et personnel administratif (mensuel), workflow de validation à 4 niveaux.</p>
      </div>

      {(dash?.my_pending_validations || 0) > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-5 flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-orange-900">Vous avez {dash.my_pending_validations} run(s) de paie à valider</div>
            <div className="text-sm text-orange-800 mt-1">Consultez la liste des runs ci-dessous pour approuver/rejeter.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI label="Employés paramétrés" value={dash?.employee_count || 0} icon={Users} color="text-[#1E3A8A]" bg="bg-blue-50" />
        <KPI label="Runs en attente" value={dash?.pending || 0} icon={Clock} color="text-[#EA580C]" bg="bg-orange-50" />
        <KPI label="Runs payés cette année" value={dash?.paid_this_year || 0} icon={CheckCircle2} color="text-[#166534]" bg="bg-green-50" />
        <KPI label="Masse salariale nette YTD" value={fmt(dash?.annual_total_net)} icon={TrendingUp} color="text-purple-600" bg="bg-purple-50" isCurrency />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <QuickLink to="/dashboard/paie/employes" title="Configuration employés" desc="Salaires de base, taux horaire, CNSS, impôts." color="bg-[#1E3A8A]" />
        <QuickLink to="/dashboard/paie/runs" title="Runs de paie mensuels" desc="Créer, valider, exporter les paies du mois." color="bg-[#166534]" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200"><h2 className="font-heading font-bold text-lg text-[#1E3A8A]">Derniers runs de paie</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">Période</th>
                <th className="px-4 py-2">Employés</th>
                <th className="px-4 py-2 text-right">Total Brut</th>
                <th className="px-4 py-2 text-right">Total Net</th>
                <th className="px-4 py-2">Statut</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {runs.slice(0, 10).map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-[#1E3A8A] font-bold">{r.period}</td>
                  <td className="px-4 py-2 tabular-nums">{r.employee_count}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(r.total_gross)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-[#166534]">{fmt(r.total_net)}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-2 text-right"><Link to={`/dashboard/paie/runs/${r.id}`}><Button size="sm" variant="outline">Ouvrir <ArrowRight className="h-3 w-3 ml-1" /></Button></Link></td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucun run de paie. Configurez d&apos;abord les employés, puis créez un run.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABELS = {
  draft: "Brouillon", pending_accountant: "Attente Comptable",
  pending_finance: "Attente Financier", pending_director: "Attente Directeur",
  pending_founder: "Attente Fondateur", paid: "Payé", rejected: "Rejeté",
};
const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  pending_accountant: "bg-blue-100 text-blue-800",
  pending_finance: "bg-cyan-100 text-cyan-800",
  pending_director: "bg-purple-100 text-purple-800",
  pending_founder: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

export function StatusBadge({ status }) {
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${STATUS_COLORS[status] || "bg-slate-100"}`}>{STATUS_LABELS[status] || status}</span>;
}

function KPI({ label, value, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} rounded-2xl border border-white p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className={`text-2xl font-extrabold ${color} tabular-nums`}>{value}</div>
    </div>
  );
}

function QuickLink({ to, title, desc, color }) {
  return (
    <Link to={to} className="group block bg-white border border-slate-200 hover:border-[#1E3A8A] rounded-2xl p-5 transition-all hover:shadow-lg">
      <div className={`w-10 h-10 ${color} rounded-xl mb-3 group-hover:scale-110 transition-transform`} />
      <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">{title}</div>
      <div className="text-sm text-slate-500">{desc}</div>
    </Link>
  );
}
